import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap/typeahead/typeahead';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  AddressLookupPrepop,
  CustomerType,
  IndustryType,
} from '@digital-platform/rs-addresses/domain';
import { AddressesService } from '@digital-platform/rs-addresses/services';
import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import {
  AddressLookup,
  AddressRaw,
  BusinessIndustryOption,
  CtaButton,
  ReqServicesType,
} from '@digital-platform/rs-cms/domain';
import {
  AddressLookupForm,
  businessIndustryOptions,
  DropdownItem,
} from '@digital-platform/rs-frontend/domain';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { ButtonComponent } from '../button/button.component';
import { CtaButtonComponent } from '../cta-button/cta-button.component';
import { DropdownComponent } from '../dropdown/dropdown.component';

enum RequestServicesType {
  RESIDENTIAL,
  COMMERCIAL,
  CUSTOMER_SELECT,
}

const REQ_SERVICES_TYPE_BUSINESS = 'show industry';
const REQ_SERVICES_TYPE_CUSTOMER_SELECT = 'customer select';

@UntilDestroy()
@Component({
  selector: 'digital-platform-address-lookup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbTypeaheadModule,
    ButtonComponent,
    DropdownComponent,
    CtaButtonComponent,
  ],
  templateUrl: './address-lookup.component.html',
  styleUrls: ['./address-lookup.component.scss'],
})
export class AddressLookupComponent
  extends BasePTypeComponent
  implements AddressLookup, OnInit, OnChanges
{
  public override type = 'requestServices' as const;

  // CMS specific inputs
  @Input() public ctaButton: CtaButton[] | null = [];
  @Input() public beforeFormText: AddressLookup['beforeFormText'] = '';
  @Input() public industryCode: AddressLookup['industryCode'] = null;
  @Input() public productCategory: AddressLookup['productCategory'] | null =
    null;
  @Input() public productTypeCode: AddressLookup['productTypeCode'] | null =
    null;
  @Input() public reqServicesType: AddressLookup['reqServicesType'] | null =
    null;
  @Input() public richTextHeading: AddressLookup['richTextHeading'] | null =
    null;
  @Input()
  public showBusinessTypeSelect: AddressLookup['showBusinessTypeSelect'] = false;
  @Input()
  public showProductAvailability: AddressLookup['showProductAvailability'] = false;
  @Input() public stacked: AddressLookup['stacked'] = false;

  // Angular specific inputs and properties
  @Input() public numberOfSearchResults?: number = 5;
  @Input() public primarySearchKey = 'addressLine1';

  // default address to display on inital load
  @Input() public defaultAddress?: AddressRaw;

  @Input() public industryType?: IndustryType;

  @Input() public addressLookupPrepop?: AddressLookupPrepop;

  @Output() public selectedAddress: EventEmitter<{
    address?: AddressRaw;
    searchFailed?: boolean;
  }> = new EventEmitter();

  @Output() public selectedBusinesstype: EventEmitter<
    IndustryType | undefined
  > = new EventEmitter();

  @Output() public selectedCustomerType: EventEmitter<CustomerType> =
    new EventEmitter();

  // triggered when the address lookup is valid
  @Output() public submitButtonClicked: EventEmitter<boolean> =
    new EventEmitter();

  @ViewChild('typeAheadInstance', { static: true })
  typeAheadInstance?: NgbTypeahead;
  @ViewChild('typeAheadElement') typeAheadElement?: ElementRef;

  public readonly CUSTOMER_TYPE_VALUE_HOME = 'home';
  public readonly CUSTOMER_TYPE_VALUE_BUSINESS = 'business';

  public currentSearchUrl = '';
  public addressAutoSearchUrl = '/api/v1/addresses/auto';
  public addressSearchUrl = '/api/v1/addresses';
  public searchParams: Record<string, any> = {
    useSecondarySearch: true,
    includeFullZipCode: false,
    mailability: true,
    allowPOBox: false,
    allowRRHighwayContract: false,
    keepMultimatch: 'Y',
  };

  public httpSearchParams = new HttpParams();
  public addresses: AddressRaw[] = [];
  public selectedAddressRaw: AddressRaw | undefined = undefined;
  public pageName = '';

  // Used to show/hide the business type dropdown when the customer type is changed to BUSINESS and reqServicesType is BUSINESS
  public showBusinessType$: Observable<boolean>;
  public showCustomerType$: Observable<boolean>;
  public searching = false;
  public searchFailed = false;
  public noResults = false;
  public addressLookupForm: FormGroup<AddressLookupForm>;
  public businessDropdownItems: DropdownItem[] = [];
  public businessIndustryOptions: BusinessIndustryOption[] =
    businessIndustryOptions;

  public showCustomerType = false;
  public showBusiness = false;
  public addressRichHeading: string | null | undefined;

  private requestServicesType$ = new Subject<RequestServicesType>();
  private customerType$ = new BehaviorSubject<CustomerType>(
    CustomerType.RESIDENTIAL
  );
  private businessType$ = new Subject<IndustryType>();
  private addressLookupPrepop$ = new BehaviorSubject<AddressLookupPrepop>({
    industryType: undefined,
    customerType: undefined,
  });

  private industryCode$ = new BehaviorSubject<IndustryType | null>(null);

  private addressesService = inject(AddressesService);

  constructor(private analyticsService: AnalyticsService) {
    super();

    this.addressLookupForm = new FormGroup<AddressLookupForm>({
      // set Default Customer Value to Home
      customerType: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      businessType: new FormControl('', [Validators.required]),
    });

    this.subscribeToCustomerType();
    this.subscribeToBusinessType();

    this.showCustomerType$ = this.shouldShowCustomerType();
    this.onCustomerTypeChange(this.showCustomerType$, this.customerType$);
    this.disableCustomerTypeIfNotCustomerSelect(this.requestServicesType$);
    this.setInitialCustomerTypeValue(
      this.showCustomerType$,
      this.addressLookupPrepop$
    );
    this.clearValidatorIfNotCommercial();

    this.showBusinessType$ = this.shouldShowBusinessType(
      this.requestServicesType$,
      this.customerType$
    );
    this.setInitialBusinessTypeValue(
      this.showBusinessType$,
      this.addressLookupPrepop$,
      this.industryCode$
    );
    this.onBusinessTypeChange(this.businessType$, this.showBusinessType$);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['addressLookupPrepop']?.currentValue) {
      this.addressLookupPrepop$.next(
        changes['addressLookupPrepop'].currentValue
      );
    }

    if (changes['industryCode']?.currentValue) {
      this.industryCode$.next(
        changes['industryCode'].currentValue?.industryCode as IndustryType
      );
    }

    if (changes['reqServicesType']?.currentValue !== undefined) {
      this.requestServicesType$.next(
        this.getRequestServicesType(changes['reqServicesType'].currentValue)
      );
    }
  }

  public ngOnInit() {
    this.httpSearchParams = new HttpParams({
      fromObject: {
        ...this.searchParams,
      },
    });

    this.currentSearchUrl = this.addressAutoSearchUrl;

    if (this.richTextHeading && (this.richTextHeading?.length || 0) > 0) {
      this.addressRichHeading = this.richTextHeading[0]?.body;
    }

    this.businessDropdownItems = this.businessIndustryOptions.map(
      (industryDropdownItem) => ({
        text: industryDropdownItem.name,
        value: industryDropdownItem.field_industry_code,
      })
    );

    if (this.defaultAddress) {
      const addressLookUpData = {
        address: this.defaultAddress,
      };

      this.addressLookupForm.patchValue(addressLookUpData);
      this.selectedAddressRaw = this.defaultAddress;
      this.selectedAddress.emit({ address: this.defaultAddress });
      this.addresses = [this.defaultAddress];
    }
  }

  public inputFormatter(address: AddressRaw) {
    return address.display || '';
  }

  public clearAddress(resetForm = true) {
    if (resetForm) {
      this.addressLookupForm.controls.address.reset();
    }
    this.selectedAddressRaw = undefined;
    this.addresses = [];

    // Sending an empty address has the effect of clearing the selectedAddress on the parent
    this.selectedAddress.emit({ address: undefined });
  }

  public isFormInvalid(): boolean {
    const isBusinessTypeInvalid = !(
      this.addressLookupForm.controls.businessType.disabled ||
      (this.addressLookupForm.controls.businessType.enabled &&
        this.addressLookupForm.controls.businessType.value &&
        this.addressLookupForm.controls.businessType.value !==
          'Select Business Type')
    );
    const isCustomerTypeInValid = !(
      this.addressLookupForm.controls.customerType.disabled ||
      (this.addressLookupForm.controls.customerType.valid &&
        this.addressLookupForm.controls.customerType.enabled)
    );

    return (
      !this.selectedAddressRaw?.addressLine1 ||
      !this.selectedAddressRaw?.postalCode ||
      isBusinessTypeInvalid ||
      isCustomerTypeInValid
    );
  }

  public onSelectedAddress(
    address?: NgbTypeaheadSelectItemEvent<AddressRaw>,
    cta = false
  ): void {
    // Upon selection, change the search url to /addresses and search again to get the full AddressRaw data
    this.currentSearchUrl = this.addressSearchUrl;

    this.serviceSearch(address?.item.display || '')
      .pipe(
        // Called when the request is canceled or completed
        finalize(() => {
          this.currentSearchUrl = this.addressAutoSearchUrl;
        })
      )
      .subscribe({
        next: (addressList: AddressRaw[]) => {
          // If a full AddressRaw was found at /addresses, emit that
          if (addressList.length > 0) {
            this.selectedAddressRaw = addressList[0];
            this.selectedAddress.emit({ address: addressList[0] });
            // Otherwise, emit the partial AddressRaw from /auto
          } else {
            this.selectedAddressRaw = address?.item;
            this.selectedAddress.emit({ address: address?.item });
          }
          if (cta) {
            this.triggerSearch();
          }
        },
        error: (e) => {
          this.searchFailed = true;
          this.selectedAddress.emit({ searchFailed: true });

          // Clear the selected address by sending an empty event
          this.clearAddress(false);
          console.error(e);
        },
      });
  }

  public updateAddressInput(cta = false): void {
    let selectedAddress = this.selectedAddressRaw;
    if (!selectedAddress && this.addresses.length === 1) {
      selectedAddress = this.addresses[0];
    }

    if (selectedAddress) {
      if (this.typeAheadInstance) {
        // Write the value on the input field
        this.typeAheadInstance.writeValue(selectedAddress);
      }

      this.onSelectedAddress(
        {
          item: selectedAddress,
          preventDefault: () => {},
        },
        cta
      );
    }
  }

  public search = (text$: Observable<string>): Observable<AddressRaw[]> =>
    text$.pipe(debounceTime(300), distinctUntilChanged()).pipe(
      tap(() => {
        if (this.typeAheadElement?.nativeElement.value) {
          this.searching = true;
          this.selectedAddressRaw = undefined;
        }
      }),
      switchMap((term) =>
        this.serviceSearch(term).pipe(
          finalize(() => {
            this.searching = false;
          }),
          tap((addresses) => {
            this.searchFailed = false;
            if (term && addresses.length === 0) {
              this.noResults = true;
            } else {
              this.noResults = false;
            }
          }),
          map((addresses: AddressRaw[]) => {
            this.setAddresses(addresses);
            return this.addresses;
          }),
          catchError(() => {
            this.searchFailed = true;
            this.selectedAddress.emit({ searchFailed: true });
            return of([]);
          })
        )
      )
    );

  private onBusinessTypeChange(
    businessType$: Subject<IndustryType>,
    showBusinessType$: Observable<boolean>
  ) {
    combineLatest([businessType$, showBusinessType$])
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(([businessType, showBusinessType]) => {
        if (showBusinessType) {
          this.selectedBusinesstype.emit(businessType);
        } else {
          this.selectedBusinesstype.emit(undefined);
        }
      });
  }

  private setInitialBusinessTypeValue(
    showBusinessType$: Observable<boolean>,
    addressLookupPrepop$: BehaviorSubject<AddressLookupPrepop>,
    industryCode$: BehaviorSubject<IndustryType | null>
  ) {
    combineLatest([showBusinessType$, addressLookupPrepop$, industryCode$])
      .pipe(
        untilDestroyed(this),
        // set the initial value if showBusinessType is true
        filter(([showBusinessType]) => showBusinessType),
        // use the industryCode by default if it exists, otherwise use the industryType from addressLookupPrepop
        map(
          ([, addressLookupPrepop, industryCode]) =>
            industryCode ?? addressLookupPrepop.industryType ?? ''
        ),
        map(
          (industryCode) =>
            this.businessIndustryOptions.find(
              (industryOption) =>
                industryOption.field_industry_code === industryCode
            )?.name ?? ''
        ),
        distinctUntilChanged()
      )
      .subscribe((industryCode) => {
        this.addressLookupForm.controls.businessType?.setValue(industryCode);
      });
  }

  private shouldShowBusinessType(
    requestServicesType$: Subject<RequestServicesType>,
    customerType$: BehaviorSubject<CustomerType>
  ): Observable<boolean> {
    const showBusinessType$ = combineLatest([
      requestServicesType$,
      customerType$,
    ]).pipe(
      untilDestroyed(this),
      map(
        // show business type if requestServicesType is commercial or if requestServicesType is customer select and customer type is commercial
        ([requestServicesType, customerType]) =>
          requestServicesType === RequestServicesType.COMMERCIAL ||
          (requestServicesType === RequestServicesType.CUSTOMER_SELECT &&
            customerType === CustomerType.COMMERCIAL)
      ),
      distinctUntilChanged()
    );

    showBusinessType$.subscribe((showBusiness) => {
      if (showBusiness) {
        this.addressLookupForm.controls.businessType.enable();
      } else {
        this.addressLookupForm.controls.businessType.disable();
      }
      this.showBusiness = showBusiness;
    });

    return showBusinessType$;
  }

  private clearValidatorIfNotCommercial() {
    this.requestServicesType$
      .pipe(
        untilDestroyed(this),
        // only emit values if requestServicesType is not commercial
        filter(
          (requestServicesType: RequestServicesType) =>
            requestServicesType !== RequestServicesType.COMMERCIAL
        ),
        distinctUntilChanged()
      )
      .subscribe(() => {
        // clear business type validator
        this.addressLookupForm.controls.businessType.clearValidators();
      });
  }

  private setInitialCustomerTypeValue(
    showCustomerType$: Observable<boolean>,
    addressLookupPrepop$: Observable<AddressLookupPrepop>
  ) {
    combineLatest([showCustomerType$, addressLookupPrepop$])
      .pipe(
        untilDestroyed(this),
        // only emit values if showCustomerType is true
        filter(([showCustomerType]) => showCustomerType),
        // use the customer type from addressLookupPrepop if it exists, otherwise use the default value
        map(
          ([, addressLookupPrepop]) =>
            addressLookupPrepop.customerType ?? CustomerType.RESIDENTIAL
        ),
        // convert the customer type to the value used by the form
        map((customerType) =>
          customerType === CustomerType.COMMERCIAL
            ? this.CUSTOMER_TYPE_VALUE_BUSINESS
            : this.CUSTOMER_TYPE_VALUE_HOME
        ),
        distinctUntilChanged()
      )
      .subscribe((customerType) => {
        this.addressLookupForm.controls.customerType.setValue(customerType);
      });
  }

  private disableCustomerTypeIfNotCustomerSelect(
    requestServicesType$: Subject<RequestServicesType>
  ) {
    requestServicesType$
      .pipe(
        untilDestroyed(this),
        filter(
          (requestServicesType: RequestServicesType) =>
            requestServicesType !== RequestServicesType.CUSTOMER_SELECT
        ),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.addressLookupForm.controls.customerType.disable();
      });
  }

  private onCustomerTypeChange(
    showCustomerType$: Observable<boolean>,
    customerType$: Observable<CustomerType>
  ) {
    combineLatest([showCustomerType$, customerType$])
      .pipe(
        untilDestroyed(this),
        // only emit values if showCustomerType is true
        filter(([showCustomerType]) => showCustomerType),
        map(([, customerType]) => customerType),
        distinctUntilChanged()
      )
      .subscribe((customerType: CustomerType) => {
        // reset business type when customer type changes
        this.addressLookupForm.controls.businessType.reset();

        // if customer type is residential, remove business type validator
        if (customerType === CustomerType.RESIDENTIAL) {
          this.addressLookupForm.controls.businessType.clearValidators();
          this.addressLookupForm.controls.businessType.updateValueAndValidity();
        } else {
          // if customer type is commercial, add business type validator
          this.addressLookupForm.controls.businessType.addValidators(
            Validators.required
          );
          this.addressLookupForm.controls.businessType.updateValueAndValidity();
        }
        this.selectedCustomerType.emit(customerType);
      });
  }

  private shouldShowCustomerType(): Observable<boolean> {
    const showCustomerType$ = this.requestServicesType$.pipe(
      untilDestroyed(this),
      map(
        (requestServicesType: RequestServicesType) =>
          requestServicesType === RequestServicesType.CUSTOMER_SELECT
      ),
      distinctUntilChanged()
    );

    showCustomerType$.subscribe((showCustomerType: boolean) => {
      if (showCustomerType) {
        this.addressLookupForm.controls.customerType.enable();
      } else {
        this.addressLookupForm.controls.customerType.disable();
      }
      this.showCustomerType = showCustomerType;
    });

    return showCustomerType$;
  }

  private subscribeToCustomerType() {
    this.addressLookupForm.controls.customerType.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((customerTypeValue: string | null) => !!customerTypeValue),
        distinctUntilChanged()
      )
      .subscribe((customerTypeValue: string | null) => {
        const customerType: CustomerType =
          customerTypeValue === this.CUSTOMER_TYPE_VALUE_BUSINESS
            ? CustomerType.COMMERCIAL
            : CustomerType.RESIDENTIAL;
        this.customerType$.next(customerType);
      });
  }

  private subscribeToBusinessType() {
    this.addressLookupForm.controls.businessType.value$
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((businessType) => {
        const businessTypeObj = this.businessIndustryOptions.find(
          (businessTypeOption) => businessTypeOption.name === businessType
        );

        this.businessType$.next(
          businessTypeObj?.field_industry_code as IndustryType
        );
      });
  }

  private getRequestServicesType(
    reqServicesType: ReqServicesType
  ): RequestServicesType {
    if (reqServicesType?.serviceType === REQ_SERVICES_TYPE_BUSINESS) {
      return RequestServicesType.COMMERCIAL;
    }

    if (reqServicesType?.serviceType === REQ_SERVICES_TYPE_CUSTOMER_SELECT) {
      return RequestServicesType.CUSTOMER_SELECT;
    }

    return RequestServicesType.RESIDENTIAL;
  }

  private serviceSearch(term: string): Observable<AddressRaw[]> {
    // don't search empty terms
    if (term === '') {
      this.clearAddress();

      return of([]);
    }

    return this.addressesService.searchForAddresses(
      term,
      this.httpSearchParams,
      this.currentSearchUrl,
      this.primarySearchKey
    );
  }

  private setAddresses(addresses: AddressRaw[]): void {
    this.addresses = addresses.slice(0, this.numberOfSearchResults);
  }

  private triggerSearch(): void {
    if (
      this.isFormInvalid() ||
      !this.selectedAddressRaw?.addressLine1 ||
      !this.selectedAddressRaw?.postalCode
    ) {
      return;
    }

    this.pageName = window.location.pathname;

    this.analyticsService.trackAddressGateSubmit(
      this.selectedAddressRaw.addressLine1,
      this.selectedAddressRaw.postalCode,
      this.showBusinessTypeSelect
        ? this.addressLookupForm.controls.businessType.value
        : '',
      this.pageName
    );
    // Close popup if it's currently open to prevent a cancelled HTTP request upon focus below
    if (this.typeAheadInstance?.isPopupOpen()) {
      this.typeAheadInstance?.dismissPopup();
    }

    if (
      this.submitButtonClicked &&
      !(this.searchFailed || this.noResults) &&
      !this.isFormInvalid()
    ) {
      this.submitButtonClicked.emit(true);
    }

    // Ensure input has focus so the user can start typing
    this.typeAheadElement?.nativeElement.focus();
  }
}
